import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesAccountDayJobStatsTypeahead,
  getV1CompaniesCompanyIdDriversTypeahead,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FilterMenuItemLoadingReason } from '~components/Filters/FilterMenuItem';
import { SearchBarHandler } from '~components/Search/SearchBar';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { JobAssignmentType } from '~constants/enums';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import connection from '~services/connectionModule';
import { extractPagination, Pagination } from '~services/pagination';
import { getDispatchStatusDate } from '~store/OrdersDispatchStore';

const PAGINATION_LIMIT = 20;

export type SelectedAssignment = {
  entity: DriverTypeahead | AccountCapacityTypeahead;
  id: string;
  type: JobAssignmentType;
};

type QueryParamsState = {
  shouldFetch: boolean;
  filters: { query: string; type: JobAssignmentType };
  pagination?: Pagination & { hasMore: boolean };
};

export default function useAssignDriverMenu(
  companyId: string,
  defaultSelectedTab: JobAssignmentType = JobAssignmentType.DRIVER,
  filterDate?: string,
) {
  const baseQueryKey = 'UseAssignDriverMenu';
  const driversQueryKey = `${baseQueryKey}_drivers`;
  const vendorsQueryKey = `${baseQueryKey}_vendors`;

  const queryClient = useQueryClient();
  const searchBarRef = useRef<SearchBarHandler>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '', type: defaultSelectedTab },
  });

  const driversQuery = useInfiniteQuery({
    queryKey: [driversQueryKey, queryParams.filters.query, companyId, filterDate],
    initialPageParam: undefined,
    getNextPageParam: () => queryParams.pagination,
    queryFn:
      queryParams.shouldFetch && queryParams.filters.type === JobAssignmentType.DRIVER
        ? async ({ pageParam, signal }) => {
            try {
              const response = await getV1CompaniesCompanyIdDriversTypeahead({
                path: { 'company-id': companyId },
                query: {
                  'filter[date]': filterDate,
                  'filter[dispatchable]': true,
                  'page[after]': pageParam?.after,
                  'page[limit]': PAGINATION_LIMIT,
                  'search[query]': queryParams.filters.query || undefined,
                },
                signal,
              });
              const drivers = response.data.data.map((driver) => {
                return DriverTypeahead.parse(driver);
              });
              const pagination = extractPagination(response);

              setQueryParams((state) => ({
                ...state,
                pagination: { ...pagination, hasMore: Boolean(pagination.after) },
              }));

              return drivers;
            } catch (error) {
              if (axios.isCancel(error)) {
                return [];
              }

              connection.handleRequestError(
                error,
                t('error_messages.drivers.failed_to_fetch_drivers'),
              );

              return [];
            }
          }
        : skipToken,
  });

  const vendorsQuery = useInfiniteQuery({
    queryKey: [vendorsQueryKey, queryParams.filters.query, companyId, filterDate],
    initialPageParam: undefined,
    getNextPageParam: () => queryParams.pagination,
    queryFn:
      queryParams.shouldFetch && queryParams.filters.type === JobAssignmentType.VENDOR
        ? async ({ pageParam, signal }) => {
            try {
              const response = await getV1CompaniesAccountDayJobStatsTypeahead({
                path: { 'company-id': companyId },
                query: {
                  date: getDispatchStatusDate({ startDate: filterDate }),
                  'page[after]': pageParam?.after,
                  'page[limit]': PAGINATION_LIMIT,
                  'search[query]': queryParams.filters.query || undefined,
                },
                signal,
              });
              const accounts = response.data.data.map((accountDayJobStat) => {
                return AccountCapacityTypeahead.parse(accountDayJobStat);
              });
              const pagination = extractPagination(response);

              setQueryParams((state) => ({
                ...state,
                pagination: { ...pagination, hasMore: Boolean(pagination.after) },
              }));

              return accounts;
            } catch (error) {
              if (axios.isCancel(error)) {
                return [];
              }

              connection.handleRequestError(
                error,
                t('error_messages.account.failed_to_fetch_company_accounts'),
              );

              return [];
            }
          }
        : skipToken,
  });

  const activeQuery = useMemo(() => {
    if (queryParams.filters.type === JobAssignmentType.DRIVER) {
      return driversQuery;
    }

    return vendorsQuery;
  }, [queryParams.filters.type, driversQuery, vendorsQuery]);

  const data: SelectedAssignment[] = (activeQuery.data?.pages?.flat() ?? []).map(
    (entity) => {
      return { entity, id: entity.id, type: queryParams.filters.type };
    },
  );
  const fetchMore =
    queryParams.pagination?.hasMore &&
    !activeQuery.isFetchingNextPage &&
    !activeQuery.isFetching
      ? activeQuery.fetchNextPage
      : undefined;
  const loadingReason = useMemo(() => {
    if (activeQuery.isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (activeQuery.isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [activeQuery.isFetchingNextPage, activeQuery.isFetching]);

  const applyDebouncedFilter = debounce((callback: () => void) => {
    callback();
  }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

  const clearAllQueriesData = useCallback(() => {
    [driversQueryKey, vendorsQueryKey].forEach((key) => {
      queryClient.setQueriesData({ queryKey: [key] }, () => ({
        pages: [],
        pageParams: [],
      }));
    });
  }, [queryClient, driversQueryKey, vendorsQueryKey]);

  const setSelectedTab = useCallback(
    (tab: JobAssignmentType) => {
      clearAllQueriesData();
      setQueryParams((state) => ({
        shouldFetch: true,
        filters: { query: state.filters.query, type: tab },
      }));
    },
    [clearAllQueriesData],
  );

  const setSearchFieldValue = useCallback((value: string) => {
    setQueryParams((state) => ({
      shouldFetch: true,
      filters: { ...state.filters, query: value },
    }));
  }, []);

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setQueryParams({
      shouldFetch: false,
      filters: { query: '', type: defaultSelectedTab },
    });
    clearAllQueriesData();
  }, [clearAllQueriesData, defaultSelectedTab]);

  useEffect(() => {
    if (isMenuOpen) {
      searchBarRef.current?.focus();
      setQueryParams((state) => ({ ...state, shouldFetch: true }));
    }
  }, [isMenuOpen, queryParams.filters.type]);

  return {
    applyDebouncedFilter,
    closeMenu,
    data,
    fetchMore,
    hasMore: queryParams.pagination?.hasMore,
    isFetchedAfterMount: activeQuery.isFetchedAfterMount,
    isMenuOpen,
    loadingReason,
    openMenu,
    searchBarRef,
    searchFieldValue: queryParams.filters.query,
    selectedTab: queryParams.filters.type,
    setSearchFieldValue,
    setSelectedTab,
  };
}
